import { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { ArrowUpRight, Globe, EnvelopeSimple } from '@phosphor-icons/react';
import { PageHero, Reveal, Chapter } from '../lib/motion';
import { Field, inputCls, VoltButton, SuccessPanel } from '../components/site/FormBits';
import { SERVICES, COUNTRIES } from '../data/site';
import { API, track } from '../lib/api';
import { useSEO } from '../lib/seo';

const TOTAL_SLOTS = 30;

const Advertise = () => {
  useSEO({
    title: 'Advertise Your Services | Vox Meridian',
    description: 'Thirty showcase slots for service providers across 10 markets. Founder-reviewed listings, free during the pilot phase.',
    path: '/advertise',
    jsonLd: {
      '@context': 'https://schema.org',
      '@type': 'CollectionPage',
      name: 'Vox Meridian Global Showcase',
      description: 'Thirty showcase slots for service providers across ten markets.',
      url: 'https://voxmeridian.com/advertise',
      isPartOf: { '@type': 'WebSite', name: 'Vox Meridian', url: 'https://voxmeridian.com' },
    },
  });

  const [ads, setAds] = useState([]);
  const [form, setForm] = useState({ company: '', trade: '', country: 'SG', tagline: '', email: '', website: '' });
  const [submitting, setSubmitting] = useState(false);
  const [slotWon, setSlotWon] = useState(null);

  const load = () => axios.get(`${API}/ads`).then((r) => setAds(r.data.ads || [])).catch(() => {});
  useEffect(() => { load(); }, []);

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });
  const bySlot = Object.fromEntries(ads.map((a) => [a.slot, a]));

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/ads`, form);
      track('conversion', { type: 'showcase_ad' });
      setSlotWon(r.data.slot);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Submission failed. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="advertise-page">
      <PageHero
        eyebrow="Global showcase"
        lines={[<>30 slots.</>, <span key="l2">Every trade. <span className="text-volt">Every country.</span></span>]}
        sub="Put your industrial business in front of buyers across 10 markets — Singapore, Malaysia, UAE, UK, Australia, US, Canada, South Africa, India and Europe. Fixed slots, first come. Free during the pilot phase; founding advertisers keep slot priority when paid plans launch."
      />

      <section className="border-b border-line bg-ink">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
          <Chapter no="01" label="The board" title={ads.length === 0 ? 'The founding 30.' : `${ads.length} of 30 slots taken.`} />
          {ads.length === 0 ? (
            <Reveal delay={0.05}>
              <div data-testid="showcase-empty-invite" className="mt-12 border border-volt/40 bg-surface p-10 sm:p-14">
                <p className="mono-label text-volt">Founding advertisers wanted</p>
                <h3 className="mt-4 max-w-2xl font-display text-3xl font-black tracking-tight text-parchment sm:text-4xl">
                  This board opens with its first verified companies.
                </h3>
                <p className="mt-5 max-w-2xl text-base leading-relaxed text-steel">
                  Thirty fixed slots across 65 services and 10 markets. Every listing is reviewed by the
                  founder before it goes live — the first companies are being verified now. Claim a slot
                  below and your business is on the board from day one, in front of every buyer who visits.
                </p>
                <div className="mt-8 flex flex-wrap items-center gap-6">
                  <a
                    href="#advertise-form"
                    data-testid="showcase-invite-cta"
                    className="group inline-flex items-center gap-2 bg-volt px-7 py-4 font-mono text-xs font-bold uppercase tracking-[0.15em] text-ink transition-transform duration-200 hover:-translate-y-0.5 hover:bg-voltdark"
                  >
                    Claim slot 01
                    <ArrowUpRight size={15} weight="bold" className="transition-transform duration-200 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </a>
                  <span className="font-mono text-xs uppercase tracking-[0.2em] text-steel" data-testid="showcase-slots-counter">
                    0 of 30 founding slots claimed
                  </span>
                </div>
              </div>
            </Reveal>
          ) : (
            <div className="mt-12 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: TOTAL_SLOTS }, (_, i) => i + 1).map((n) => {
                const ad = bySlot[n];
                return (
                  <Reveal key={n} delay={Math.min(n * 0.03, 0.4)} y={16}>
                    {ad ? (
                      <div data-testid={`ad-slot-${n}`} className="flex h-full flex-col border border-volt/40 bg-surface p-6">
                        <div className="flex items-center justify-between">
                          <span className="font-mono text-xs font-semibold text-volt">SLOT {String(n).padStart(2, '0')}</span>
                          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-steel">{COUNTRIES.find((c) => c.code === ad.country)?.name || ad.country}</span>
                        </div>
                        <p className="mt-4 font-display text-lg font-bold text-parchment">{ad.company}</p>
                        <p className="mt-1 font-mono text-xs uppercase tracking-[0.15em] text-volt/80">{ad.trade}</p>
                        <p className="mt-3 flex-1 text-sm leading-relaxed text-steel">{ad.tagline}</p>
                        <div className="mt-4 flex items-center gap-4 border-t border-line pt-4">
                          <a href={`mailto:${ad.email}`} data-testid={`ad-slot-${n}-email`} className="flex items-center gap-1.5 text-xs text-steel transition-colors duration-200 hover:text-volt">
                            <EnvelopeSimple size={14} /> Contact
                          </a>
                          {ad.website && (
                            <a href={ad.website.startsWith('http') ? ad.website : `https://${ad.website}`} target="_blank" rel="noopener noreferrer" data-testid={`ad-slot-${n}-website`} className="flex items-center gap-1.5 text-xs text-steel transition-colors duration-200 hover:text-volt">
                              <Globe size={14} /> Website
                            </a>
                          )}
                        </div>
                      </div>
                    ) : (
                      <a href="#advertise-form" data-testid={`ad-slot-${n}-open`} className="group flex h-full min-h-[180px] flex-col items-center justify-center border border-dashed border-line p-6 transition-colors duration-200 hover:border-volt/60">
                        <span className="font-mono text-xs font-semibold uppercase tracking-[0.2em] text-steel/60 group-hover:text-volt">Slot {String(n).padStart(2, '0')} — Open</span>
                        <span className="mt-2 flex items-center gap-1.5 text-xs text-steel/40 transition-colors duration-200 group-hover:text-parchment">
                          Your business here <ArrowUpRight size={12} />
                        </span>
                      </a>
                    )}
                  </Reveal>
                );
              })}
            </div>
          )}
        </div>
      </section>

      <section className="bg-ink bg-grid" id="advertise-form">
        <div className="mx-auto max-w-7xl px-6 py-20 lg:px-10 lg:py-28">
          <div className="grid gap-14 lg:grid-cols-12">
            <div className="lg:col-span-5">
              <Chapter no="02" label="Claim a slot" title="Advertise your business." />
              <Reveal delay={0.1}>
                <p className="mt-8 max-w-md text-base leading-relaxed text-steel">
                  Company, trade, country, one sharp tagline, and how buyers reach you. Slots are
                  numbered and fixed — once the board fills, it fills. Reviewed and live within 24 hours.
                </p>
              </Reveal>
            </div>
            <Reveal delay={0.15} className="lg:col-span-7">
              {slotWon ? (
                <SuccessPanel
                  testId="advertise-success"
                  title="Submission received."
                  lines={[
                    'The founder verifies every company before it goes live — target: within 24 hours.',
                    'You will get an email the moment your listing is on the board.',
                  ]}
                />
              ) : (
                <form data-testid="advertise-form" onSubmit={submit} className="space-y-6 border border-line bg-surface p-8 sm:p-10">
                  <div className="grid gap-6 sm:grid-cols-2">
                    <Field label="Company name" htmlFor="a-company">
                      <input id="a-company" data-testid="advertise-company-input" required className={inputCls} value={form.company} onChange={set('company')} placeholder="Acme Engineering Sdn Bhd" />
                    </Field>
                    <Field label="Country" htmlFor="a-country">
                      <select id="a-country" data-testid="advertise-country-select" className={inputCls} value={form.country} onChange={set('country')}>
                        {COUNTRIES.map((c) => (
                          <option key={c.code} value={c.code}>{c.name}</option>
                        ))}
                      </select>
                    </Field>
                  </div>
                  <Field label="Trade" htmlFor="a-trade">
                    <select id="a-trade" data-testid="advertise-trade-select" required className={inputCls} value={form.trade} onChange={set('trade')}>
                      <option value="" disabled>Select your trade</option>
                      {SERVICES.map((s) => (
                        <option key={s.name} value={s.name}>{s.name}</option>
                      ))}
                    </select>
                  </Field>
                  <Field label="Tagline" htmlFor="a-tagline" hint="One line that sells you — max 120 characters.">
                    <input id="a-tagline" data-testid="advertise-tagline-input" required maxLength={120} className={inputCls} value={form.tagline} onChange={set('tagline')} placeholder="24/7 hydraulic hose replacement across Johor & Singapore" />
                  </Field>
                  <div className="grid gap-6 sm:grid-cols-2">
                    <Field label="Contact email" htmlFor="a-email">
                      <input id="a-email" data-testid="advertise-email-input" required type="email" className={inputCls} value={form.email} onChange={set('email')} placeholder="sales@company.com" />
                    </Field>
                    <Field label="Website (optional)" htmlFor="a-website">
                      <input id="a-website" data-testid="advertise-website-input" className={inputCls} value={form.website} onChange={set('website')} placeholder="company.com" />
                    </Field>
                  </div>
                  <VoltButton type="submit" data-testid="advertise-submit-button" disabled={submitting} className="w-full">
                    {submitting ? 'Claiming…' : 'Claim my slot — free during pilot'}
                  </VoltButton>
                  <p className="text-center text-xs text-steel/50">
                    Listings are reviewed before going live. Free during the pilot — no obligation to
                    continue on paid plans later. See <a href="/terms" className="underline transition-colors duration-200 hover:text-volt">Terms</a>.
                  </p>
                </form>
              )}
            </Reveal>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Advertise;
